import { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useResetRequests, useReviewResetRequest } from '../hooks/useAgreementsAndResets';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from '@/shared/lib/toast';

type ResetStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

const STATUS_LABELS: Record<ResetStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
};

const getStatusVariant = (status: ResetStatus): 'success' | 'default' | 'error' => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'default';
    case 'REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

export function ResetRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<ResetStatus | 'ALL'>('ALL');

  const { data: requestsData, isLoading } = useResetRequests({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
  });

  const requests = requestsData?.data ?? [];

  const reviewReset = useReviewResetRequest();

  const handleApprove = async (requestId: string) => {
    if (!confirm('Confirma a aprovação desta solicitação de reset? O colaborador voltará ao grau D0.')) {
      return;
    }

    try {
      await reviewReset.mutateAsync({
        id: requestId,
        data: { status: 'APPROVED' },
      });
      toast.success('Reset aprovado com sucesso');
    } catch (error) {
      toast.error('Erro ao aprovar reset');
    }
  };

  const handleReject = async (requestId: string) => {
    const reason = prompt('Informe o motivo da rejeição:');
    if (!reason?.trim()) {
      toast.error('É necessário informar um motivo para rejeitar');
      return;
    }

    try {
      await reviewReset.mutateAsync({
        id: requestId,
        data: { status: 'REJECTED', managerNotes: reason.trim() },
      });
      toast.success('Reset rejeitado');
    } catch (error) {
      toast.error('Erro ao rejeitar reset');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Solicitações de Reset</h1>
        <p className="text-muted-foreground">Pedidos de reset após 12 meses sem incidentes</p>
      </div>

      {/* Info Card */}
      <div className="flex gap-3 p-4 border border-border rounded-lg bg-card">
        <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0" />
        <div className="text-sm">
          <p className="font-medium mb-1">Sobre o Reset de Grau</p>
          <p className="text-muted-foreground">
            Colaboradores que completam 12 meses consecutivos sem novos incidentes podem solicitar
            o reset do grau disciplinar, voltando ao D0. Esta é uma forma de reconhecer a mudança
            de comportamento e dar uma nova chance.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const).map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                onClick={() => setStatusFilter(status)}
                size="sm"
              >
                {status === 'ALL' ? 'Todos' : STATUS_LABELS[status]}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <div className="grid gap-4">
        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              Nenhuma solicitação encontrada
            </CardContent>
          </Card>
        ) : (
          requests.map((request) => (
            <Card key={request.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">
                        {request.employee?.user.name}
                      </CardTitle>
                      <Badge variant={getStatusVariant(request.status)}>
                        {STATUS_LABELS[request.status]}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">
                      Solicitado em{' '}
                      {format(new Date(request.requestedAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Employee Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Grau Atual</p>
                    <p className="font-medium text-lg">{request.currentDegree}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total de Incidentes</p>
                    <p className="font-medium text-lg">{request.totalIncidents}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Último Incidente</p>
                    <p className="font-medium text-lg">
                      {request.daysSinceLastIncident} dias atrás
                    </p>
                  </div>
                </div>

                {/* Justification */}
                {request.justification && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-2">Justificativa</p>
                    <p className="text-gray-700">{request.justification}</p>
                  </div>
                )}

                {/* Review Info */}
                {request.status !== 'PENDING' && (
                  <div className="pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">
                          {request.status === 'APPROVED' ? 'Aprovado por' : 'Rejeitado por'}
                        </p>
                        <p className="font-medium">{request.reviewedBy?.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Data da Revisão</p>
                        <p className="font-medium">
                          {request.reviewedAt &&
                            format(new Date(request.reviewedAt), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                    {request.status === 'REJECTED' && request.rejectionReason && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-500">Motivo da Rejeição</p>
                        <p className="text-red-600">{request.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                {request.status === 'PENDING' && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(request.id)}
                      disabled={reviewReset.isPending}
                    >
                      {reviewReset.isPending ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(request.id)}
                      disabled={reviewReset.isPending}
                    >
                      {reviewReset.isPending ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      Rejeitar
                    </Button>
                  </div>
                )}

                {/* Eligibility Warning */}
                {request.status === 'PENDING' && request.daysSinceLastIncident < 365 && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600 shrink-0" />
                      <div className="text-sm text-orange-800">
                        <p className="font-medium">Atenção</p>
                        <p>
                          Ainda faltam {365 - request.daysSinceLastIncident} dias para completar os
                          12 meses necessários. Considere aguardar antes de aprovar.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {request.status === 'APPROVED' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium">Reset Aprovado</p>
                        <p>
                          O colaborador foi resetado para o grau D0. O histórico de incidentes foi
                          mantido para fins de registro.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
